/* eslint-disable react-hooks/exhaustive-deps */
import { useCallback, useContext, useEffect, useState } from 'react'
import { useLocation, useParams } from 'react-router-dom'
import { useFetchOne } from '@/utils/api/useFetch'
import { TenantFeature } from '@/utils/Tenant'
import { TestAnswerDto } from 'api/Clients/Types/TestDto'
import { clientTestTypes } from 'api/Clients/Types/TestQuestionDto'
import { PermissionsEnum } from 'api/Identity/Types/PermissionsEnum'
import { useApiClient } from 'utils/api/ApiClient'
import { ClientsApiClient } from 'utils/api/clientsApi'
import { AuthSessionContext } from 'utils/AuthContext'
import { canEditEconomicProfileTestForClientStatus } from 'utils/clients.helpers'
import { handleOpenWindow } from 'utils/openTestWindow'
import { hasPermission } from 'utils/permissionsValidator'

import ClientTestFilled from './ClientTestFilled'

interface TestModalProps {
  data: TestAnswerDto
  allowEdit: boolean
  visible: boolean
}

const ClientTestFilledWindow = (): JSX.Element => {
  const params = useParams<{
    type: clientTestTypes
    clientId: string
    testId: string
  }>()

  const clientsApiClient = useApiClient(ClientsApiClient)
  const [auth] = useContext(AuthSessionContext)
  const location = useLocation()
  const clientCallback = useCallback(async () => {
    if (params.type === clientTestTypes.economicProfile && params.clientId) {
      return await clientsApiClient.getClient(params.clientId)
    }
    return null
  }, [params.type, params.clientId])

  const { data: client } = useFetchOne(clientCallback)

  const [testDataModal, setTestDataModal] = useState<TestModalProps | null>(null)
  const [selectedTestAnswer, setSelectedTestAnswer] = useState<TestAnswerDto | null>(null)
  const [canWrite, setCanWrite] = useState<boolean>(false)

  const permissions = auth?.permissions || []

  useEffect(() => {
    if (params.type === clientTestTypes.client)
      setCanWrite(hasPermission(permissions, PermissionsEnum.CanAddClientAppropriatenessTest))
    if (params.type === clientTestTypes.kyc)
      setCanWrite(hasPermission(permissions, PermissionsEnum.CanAddClientKycUpdateTests))
    if (params.type === clientTestTypes.ib)
      setCanWrite(hasPermission(permissions, PermissionsEnum.CanAddClientIBInformation))
    if (params.type === clientTestTypes.classification) {
      setCanWrite(hasPermission(permissions, PermissionsEnum.CanAddClientClassification))
    }
    if (params.type === clientTestTypes.economicProfile) {
      setCanWrite(
        !!client?.status.id &&
          hasPermission(permissions, PermissionsEnum.CanEditEconomicProfileTest) &&
          canEditEconomicProfileTestForClientStatus(client.status.id)
      )
    }
    if (params.type === clientTestTypes.corporate) {
      setCanWrite(true)
    }
  }, [params.type, client?.status.id])

  useEffect(() => {
    if (params.testId) setTestAnswer(params.testId)
  }, [params.testId])

  const setTestAnswer = async (clientTestId: string) => {
    const clientTestAnswer = await clientsApiClient.getClientTestAnswer(clientTestId)
    setSelectedTestAnswer(clientTestAnswer)
    if (params.type === clientTestTypes.client)
      setCompletedTest(
        clientTestAnswer,
        hasPermission(permissions, PermissionsEnum.CanAddClientAppropriatenessTest)
      )
    if (params.type === clientTestTypes.economicProfile) {
      setCompletedTest(
        clientTestAnswer,
        hasPermission(permissions, PermissionsEnum.CanEditEconomicProfileTest)
      )
    }
    if (params.type === clientTestTypes.kyc)
      setCompletedTest(
        clientTestAnswer,
        hasPermission(permissions, PermissionsEnum.CanAddClientKycUpdateTests)
      )
    if (params.type === clientTestTypes.ib)
      setCompletedTest(
        clientTestAnswer,
        hasPermission(permissions, PermissionsEnum.CanAddClientIBInformation)
      )
    if (params.type === clientTestTypes.classification)
      setCompletedTest(
        clientTestAnswer,
        hasPermission(permissions, PermissionsEnum.CanAddClientClassification)
      )
    if (params.type === clientTestTypes.corporate) setCompletedTest(clientTestAnswer, true)
  }

  const setCompletedTest = (dataItem: TestAnswerDto, allowEdit: boolean) => {
    return setTestDataModal({
      data: dataItem,
      allowEdit,
      visible: true,
    })
  }

  const handleAllowRetake = async (clientTestId: string) => {
    return await clientsApiClient.allowRetakeClientTest(clientTestId)
  }

  const handleOpenTestForm = () => {
    const productId = new URLSearchParams(location.search).get('productId') || ''
    const subcategoryId = new URLSearchParams(location.search).get('subcategoryId') || ''

    if (params.type && params.clientId && params.testId) {
      handleOpenWindow({
        urlKey: TenantFeature.clientEditTestFormWindow,
        pathParams: { type: params.type, clientId: params.clientId, testId: params.testId },
        additionalParams: { productId, subcategoryId },
      })
    }

    window.close()
  }

  return (
    <>
      {selectedTestAnswer && (
        <ClientTestFilled
          showScore
          permissions={permissions}
          allowEdit={testDataModal?.allowEdit && canWrite}
          existingTest={selectedTestAnswer}
          allowRetake={handleAllowRetake}
          type={params.type}
          setFromExistingTest={handleOpenTestForm}
          scoreNote={selectedTestAnswer.scoreNote}
        />
      )}
    </>
  )
}

export default ClientTestFilledWindow
